import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { remove } from '../../../resource-base/APIVersion1';
import { useIndirectlyVersionedResourceAdapter } from '../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedArtifactResource,
    IndirectlyVersionedArtifactResourceSchema,
} from '../../../resource-base/artifactResourceTypes';
import { IdSchema } from '../../../resource-base/baseSchemas';
import { msObject } from '../../../resource-base/msObject';
import { ParamConstants } from '../../../resource-base/types';
import { useMutationWithDelay } from '../../../resource-base/useMutationWithDelay';

export enum UseCaseStandardAssociationPropertyValidation {
    STANDARD = 'STANDARD',
}

export const UseCaseStandardAssociationResourceSchema = IndirectlyVersionedArtifactResourceSchema.extend({
    resourceType: z
        .literal(ResourceType.USE_CASE_STANDARD_ASSOCIATION)
        .default(ResourceType.USE_CASE_STANDARD_ASSOCIATION),
    standard: IdSchema,
});
export const UseCaseStandardAssociationMutationResourceSchema = getMutationSchema(
    UseCaseStandardAssociationResourceSchema
);

export type UseCaseStandardAssociationResource = z.infer<typeof UseCaseStandardAssociationResourceSchema>;

export const defaultUseCaseStandardAssociationResource: UseCaseStandardAssociationResource = {
    ...defaultIndirectlyVersionedArtifactResource,
    resourceType: ResourceType.USE_CASE_STANDARD_ASSOCIATION,
    standard: '',
};

const DeleteUseCaseStandardAssociationSchema = msObject({
    useCaseId: IdSchema,
    standardObjectId: IdSchema,
});

export interface DeleteUseCaseStandardAssociation {
    useCaseId: string;
    standardObjectId: string;
}

export function useUseCaseStandardAssociationActions() {
    const queryClient = useQueryClient();
    const actions = {
        ...useMultiResourceSingleParentedResourceAdapterActions(
            'project/use-case-standard-association',
            ResourceType.USE_CASE_STANDARD_ASSOCIATION,
            UseCaseStandardAssociationResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter(
            'project/use-case-standard-association',
            ResourceType.USE_CASE_STANDARD_ASSOCIATION,
            UseCaseStandardAssociationResourceSchema
        ),
    };

    const useDeleteResourceByUseCaseAndStandard = () => {
        return useMutationWithDelay(
            ({ useCaseId, standardObjectId }: DeleteUseCaseStandardAssociation) =>
                remove(
                    `project/use-case-standard-association/delete-by-use-case-and-standard?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${useCaseId}&standard=${standardObjectId}`
                ).then((result) => actions.processMutationResponse(result, 'DELETE')),
            DeleteUseCaseStandardAssociationSchema,
            undefined,
            (data) => {
                queryClient.setQueryData(['lastDeletionMutation'], Date.now());
                setTimeout(() => {
                    queryClient
                        .invalidateQueries({
                            predicate: (query) =>
                                query.queryKey[0] !== 'INFINITY_STALE_TIME' && !query.queryKey.includes(data.useCaseId),
                            type: 'all',
                            refetchType: 'active',
                        })
                        .then();
                }, 100);
            }
        );
    };

    return {
        ...actions,
        useDeleteResourceByUseCaseAndStandard,
    };
}
